import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  List,
  Loader2,
  RefreshCw,
  AlertCircle,
  UserCheck,
  UserX,
  Phone,
  Mail,
  Calendar,
  Timer,
  Search,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import {
  getEventRegistrations,
  getEventRegistrationStats,
  updateRegistrationStatus,
  checkInRegistration,
  getPublicEvent,
} from "../services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

// ────────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ────────────────────────────────────────────────────────────────────────────────

const toJsDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (val?.toDate) return val.toDate();
  if (val?.seconds) return new Date(val.seconds * 1000);
  if (val?._seconds) return new Date(val._seconds * 1000);
  if (typeof val === "string") {
    const iso = new Date(val);
    if (!isNaN(iso)) return iso;
  }
  return null;
};

const formatDate = (val) => {
  const d = toJsDate(val);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatTime = (val) => {
  const d = toJsDate(val);
  if (!d) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const formatDateTime = (val) => {
  const d = toJsDate(val);
  if (!d) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ────────────────────────────────────────────────────────────────────────────────
// Status Badge Component
// ────────────────────────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const variants = {
    pending: { variant: "default", className: "bg-amber-100 text-amber-800 hover:bg-amber-100" },
    approved: { variant: "default", className: "bg-green-100 text-green-800 hover:bg-green-100" },
    rejected: { variant: "default", className: "bg-red-100 text-red-800 hover:bg-red-100" },
    waitlist: { variant: "default", className: "bg-orange-100 text-orange-800 hover:bg-orange-100" },
    checked_in: { variant: "default", className: "bg-blue-100 text-blue-800 hover:bg-blue-100" },
    cancelled: { variant: "default", className: "bg-gray-100 text-gray-800 hover:bg-gray-100" },
  };

  const config = variants[status] || variants.pending;
  const displayText = status === "checked_in" ? "Checked In" : status?.charAt(0).toUpperCase() + status?.slice(1);

  return (
    <Badge variant={config.variant} className={config.className}>
      {displayText}
    </Badge>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// Stats Card Component
// ────────────────────────────────────────────────────────────────────────────────

const StatsCard = ({ title, value, icon: Icon, color }) => (
  <Card>
    <CardContent className="flex items-center gap-4 p-4">
      <div className={`rounded-lg p-2 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm text-muted-foreground">{title}</p>
      </div>
    </CardContent>
  </Card>
);

// ────────────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────────────

export default function RegistrationManagement() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const instanceDate = searchParams.get("date");
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [processingId, setProcessingId] = useState(null);

  // Dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Fetch data
  const fetchData = useCallback(async (isRefresh = false) => {
    if (!eventId || !user) return;

    if (!isRefresh) setLoading(true);
    setError(null);

    try {
      // Fetch event details
      const eventResponse = await getPublicEvent(eventId, instanceDate);
      setEvent(eventResponse.event || eventResponse);

      // Fetch registrations
      const regResponse = await getEventRegistrations(eventId, { recurrenceInstanceId: instanceDate });
      setRegistrations(regResponse?.registrations || []);

      // Fetch statistics
      try {
        const statsResponse = await getEventRegistrationStats(eventId, instanceDate);
        setStatistics(statsResponse);
      } catch (statsErr) {
        console.error("Error fetching stats:", statsErr);
        // Stats are non-critical, continue without them
      }
    } catch (e) {
      console.error("Error fetching registration data:", e);
      setError(e.response?.data?.error || e.message || "Failed to load registration data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventId, user, instanceDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(true);
  };

  // Handle registration approval
  const handleApprove = async (registrationId) => {
    setProcessingId(registrationId);
    try {
      await updateRegistrationStatus(eventId, registrationId, "approve");
      setRegistrations(prev =>
        prev.map(r => r.id === registrationId ? { ...r, status: "approved" } : r)
      );
    } catch (e) {
      console.error("Approve error:", e);
      setError(e.response?.data?.error || "Failed to approve registration");
    } finally {
      setProcessingId(null);
    }
  };

  // Handle registration rejection
  const openRejectDialog = (registration) => {
    setSelectedRegistration(registration);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const confirmReject = async () => {
    if (!selectedRegistration) return;
    
    setProcessingId(selectedRegistration.id);
    try {
      await updateRegistrationStatus(eventId, selectedRegistration.id, "reject", rejectionReason);
      setRegistrations(prev =>
        prev.map(r => r.id === selectedRegistration.id ? { ...r, status: "rejected" } : r)
      );
      setRejectDialogOpen(false);
      setSelectedRegistration(null);
    } catch (e) {
      console.error("Reject error:", e);
      setError(e.response?.data?.error || "Failed to reject registration");
    } finally {
      setProcessingId(null);
    }
  };

  // Handle manual check-in
  const handleCheckIn = async (registrationId) => {
    setProcessingId(registrationId);
    try {
      await checkInRegistration(eventId, registrationId, instanceDate);
      setRegistrations(prev =>
        prev.map(r => r.id === registrationId 
          ? { ...r, status: "checked_in", checkedInAt: new Date().toISOString() } 
          : r)
      );
    } catch (e) {
      console.error("Check-in error:", e);
      setError(e.response?.data?.error || "Failed to check in");
    } finally {
      setProcessingId(null);
    }
  };

  // Filter registrations by tab and search
  const filteredRegistrations = useMemo(() => {
    let filtered = registrations;

    // Filter by tab
    if (activeTab === "pending") {
      filtered = filtered.filter(r => r.status === "pending");
    } else if (activeTab === "approved") {
      filtered = filtered.filter(r => r.status === "approved" && !r.checkedInAt);
    } else if (activeTab === "checked_in") {
      filtered = filtered.filter(r => r.checkedInAt);
    } else if (activeTab === "waitlist") {
      filtered = filtered.filter(r => r.status === "waitlist");
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const info = r.registrantInfo || {};
        const name = `${info.firstName || ""} ${info.lastName || ""}`.toLowerCase();
        const email = (info.email || r.userEmail || "").toLowerCase();
        const phone = (info.phone || "").toLowerCase();
        return name.includes(query) || email.includes(query) || phone.includes(query);
      });
    }

    return filtered;
  }, [registrations, activeTab, searchQuery]);

  // Counts
  const counts = useMemo(() => ({
    all: registrations.length,
    pending: registrations.filter(r => r.status === "pending").length,
    approved: registrations.filter(r => r.status === "approved" && !r.checkedInAt).length,
    checked_in: registrations.filter(r => r.checkedInAt).length,
    waitlist: registrations.filter(r => r.status === "waitlist").length,
  }), [registrations]);

  // Get registrant display info
  const getRegistrantInfo = (registration) => {
    const info = registration.registrantInfo || {};
    const firstName = info.firstName || "";
    const lastName = info.lastName || "";
    const fullName = firstName && lastName 
      ? `${firstName} ${lastName}` 
      : info.fullName || info.displayName || registration.userName || "";
    const email = info.email || registration.userEmail || "";
    const phone = info.phone || "";
    const age = info.age;
    const gender = info.gender;

    return { fullName, email, phone, age, gender };
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto max-w-6xl px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Manage Registrations</h1>
            {event && (
              <p className="text-muted-foreground">
                {event.title}
                {instanceDate && ` • ${formatDate(instanceDate)}`}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Statistics */}
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-5">
          <StatsCard
            title="Total"
            value={statistics?.totalRegistrations || counts.all}
            icon={Users}
            color="bg-gray-100 text-gray-600"
          />
          <StatsCard
            title="Pending"
            value={statistics?.pendingCount || counts.pending}
            icon={Clock}
            color="bg-amber-100 text-amber-600"
          />
          <StatsCard
            title="Approved"
            value={statistics?.approvedCount || counts.approved}
            icon={CheckCircle}
            color="bg-green-100 text-green-600"
          />
          <StatsCard
            title="Checked In"
            value={statistics?.checkedInCount || counts.checked_in}
            icon={UserCheck}
            color="bg-blue-100 text-blue-600"
          />
          <StatsCard
            title="Waitlist"
            value={statistics?.waitlistCount || counts.waitlist}
            icon={List}
            color="bg-orange-100 text-orange-600"
          />
        </div>

        {/* Search and Tabs */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-lg">Registrations</CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="all">
                  All ({counts.all})
                </TabsTrigger>
                <TabsTrigger value="pending">
                  Pending ({counts.pending})
                </TabsTrigger>
                <TabsTrigger value="approved">
                  Approved ({counts.approved})
                </TabsTrigger>
                <TabsTrigger value="checked_in">
                  Checked In ({counts.checked_in})
                </TabsTrigger>
                {counts.waitlist > 0 && (
                  <TabsTrigger value="waitlist">
                    Waitlist ({counts.waitlist})
                  </TabsTrigger>
                )}
              </TabsList>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRegistrations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <Users className="h-8 w-8" />
                            <p>No registrations {activeTab !== "all" && `with status "${activeTab}"`}</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRegistrations.map((registration) => {
                        const { fullName, email, phone, age, gender } = getRegistrantInfo(registration);
                        const isProcessing = processingId === registration.id;

                        return (
                          <TableRow key={registration.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{fullName || email || "Unknown"}</p>
                                <div className="flex gap-2 text-xs text-muted-foreground">
                                  {age && <span>Age: {age}</span>}
                                  {gender && <span>• {gender}</span>}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1 text-sm">
                                {email && (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Mail className="h-3 w-3" />
                                    <span className="truncate max-w-[180px]">{email}</span>
                                  </div>
                                )}
                                {phone && (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Phone className="h-3 w-3" />
                                    <span>{phone}</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={registration.checkedInAt ? "checked_in" : registration.status} />
                              {registration.checkedInAt && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {formatTime(registration.checkedInAt)}
                                </p>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>{formatDateTime(registration.registeredAt)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {registration.status === "pending" && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openRejectDialog(registration)}
                                      disabled={isProcessing}
                                      className="text-destructive hover:text-destructive"
                                    >
                                      {isProcessing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <UserX className="h-4 w-4" />
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleApprove(registration.id)}
                                      disabled={isProcessing}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      {isProcessing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <>
                                          <CheckCircle className="mr-1 h-4 w-4" />
                                          Approve
                                        </>
                                      )}
                                    </Button>
                                  </>
                                )}
                                {registration.status === "approved" && !registration.checkedInAt && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleCheckIn(registration.id)}
                                    disabled={isProcessing}
                                  >
                                    {isProcessing ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <>
                                        <UserCheck className="mr-1 h-4 w-4" />
                                        Check In
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Reject Confirmation Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Registration</DialogTitle>
              <DialogDescription>
                Are you sure you want to reject this registration? You can optionally provide a reason.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                placeholder="Reason for rejection (optional)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmReject}
                disabled={processingId}
              >
                {processingId ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
